import { useEffect, useState } from 'react';
import { useWidgetStore, WidgetView } from './store/widgetStore';
import {
  initMessagingService,
  MessageType,
  initResizeService,
  initCSPService,
  initSecurityHeaders,
  getErrorHandlingService,
} from './services';
import type { WidgetConfig, Provider, AppointmentSlot } from './types';
import { LoadingSpinner, ErrorDisplay } from './components/ui';
import { Navbar } from './components/layout';
import { BookingPage, ConfirmationPanel, SuccessPanel } from './components/steps';
import { useWidgetSettings } from './hooks';
import { unregisterAllServiceWorkers } from './utils/serviceWorker';
import { setParentOrigin } from './utils/parentOrigin';

// View states for the new booking flow
type BookingViewState = 'booking' | 'confirmation' | 'success';

interface BookingData {
  provider: Provider;
  slot: AppointmentSlot;
  patientInfo: {
    name: string;
    phone: string;
    email: string;
    dateOfBirth: string;
    reasonForVisit: string;
    insuranceProvider: string;
    memberId: string;
    groupMember: string;
  };
  service: string;
  location: string;
  paymentMethodId?: string;
}

export default function App() {
  console.log('🎯 App component rendering...');
  
  const [servicesInitialized, setServicesInitialized] = useState(false);
  const [viewState, setViewState] = useState<BookingViewState>('booking');
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [practiceSlug, setPracticeSlug] = useState<string | null>(() => {
    // Initialize from URL on mount (for direct iframe embedding)
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const slugFromUrl = urlParams.get('slug');
      if (slugFromUrl) {
        console.log('📋 Practice slug initialized from URL:', slugFromUrl);
        return slugFromUrl;
      }
    }
    return null;
  });
  const { initialize, currentView, error, clearError, setWidgetData } = useWidgetStore();
  
  console.log('🎯 App state:', { servicesInitialized, viewState, error, practiceSlug });

  // Get practice slug from multiple sources (priority order)
  const getPracticeSlug = (): string | null => {
    // Priority 1: From postMessage (WIDGET_INIT) - most reliable (overrides URL/env)
    if (practiceSlug) {
      console.log('📋 Practice slug from state (postMessage/URL/env):', practiceSlug);
      return practiceSlug;
    }
    
    // No slug found - return null (will show error)
    console.warn('⚠️ No practice slug found. Widget will not load.');
    return null;
  };

  const resolvedPracticeSlug = getPracticeSlug();

  // Fetch widget settings (hook handles empty slug gracefully)
  const {
    data: widgetSettingsData,
    loading: widgetLoading,
    error: widgetError,
  } = useWidgetSettings(resolvedPracticeSlug || '');

  useEffect(() => {
    const initializeWidget = async () => {
      console.log('🚀 Within EHR Widget - Development Mode');
      console.log('Environment:', import.meta.env.MODE);

      // CRITICAL: Unregister all service workers BEFORE making any API calls
      // This must complete before we proceed with initialization
      const swUnregistered = await unregisterAllServiceWorkers();
      if (!swUnregistered) {
        console.warn('⚠️ [App] Service worker unregistration may have failed - API calls might be blocked');
      }
      
      // Additional wait to ensure service worker is fully stopped
      await new Promise(resolve => setTimeout(resolve, 300));

      // Initialize security services first
    try {
      // 1. Initialize CSP (Content Security Policy)
      initCSPService({
        frameAncestors: ['*'], // Development only - restrict in production
        scriptSrc: ['https://www.google.com/recaptcha/', 'https://www.gstatic.com/recaptcha/'],
        connectSrc: ['https://api.withinehr.com'],
        reportOnly: import.meta.env.DEV, // Report-only in development
      });
      console.log('✅ CSP initialized');

      // 2. Initialize security headers
      // Check if we're in embed mode (loaded via embed.html)
      const isEmbedMode = window.location.pathname.includes('embed.html');
      initSecurityHeaders({
        enforceHTTPS: import.meta.env.PROD,
        enableHSTS: import.meta.env.PROD,
        // Allow embedding in iframes when loaded via embed.html
        xFrameOptions: isEmbedMode ? 'SAMEORIGIN' : 'DENY',
      });
      console.log('✅ Security headers initialized', { isEmbedMode });

      // 3. Initialize error handling with PII filtering
      getErrorHandlingService(); // Sets up global error handlers
      console.log('✅ Secure error handling initialized');
    } catch (error) {
      console.error('Failed to initialize security services:', error);
    }

    // Initialize widget configuration
    const config: WidgetConfig = {
      apiUrl: import.meta.env.WIDGET_API_URL || 'https://api.withinehr.com',
      apiKey: import.meta.env.WIDGET_API_KEY || 'demo_key',
      containerId: 'within-widget-root',
      organizationId: 'demo_org',
      theme: {
        primaryColor: '#014952',
        darkMode: false,
      },
      security: {
        allowedOrigins: ['*'], // Development only - restrict in production
        enableRecaptchaV3: false, // Enable when keys are configured
        enableRecaptchaV2: false,
      },
      features: {
        enableBooking: true,
        enableCancellation: true,
        enableRescheduling: true,
      },
      debug: true,
    };

    // Initialize Zustand store
    initialize(config);

    // Initialize messaging service
    let messaging: ReturnType<typeof initMessagingService> | null = null;
    try {
      console.log('🔧 [App] Initializing messaging service...');
      messaging = initMessagingService({
        allowedOrigins: config.security?.allowedOrigins || ['*'],
        debug: config.debug || true, // Enable debug in development
      });
      console.log('✅ [App] Messaging service initialized');

      // Listen for initialization messages from parent (loader)
      if (messaging) {
        messaging.on(MessageType.WIDGET_INIT, (message) => {
          console.log('📨 Received WIDGET_INIT from parent:', message);
          // Extract config from message if provided
          // Type guard: check if message has config property (InitMessage type)
          if (message.type === MessageType.WIDGET_INIT && 'data' in message && message.data && typeof message.data === 'object' && 'config' in message.data) {
            const initData = message.data as { config: Record<string, unknown> };
            console.log('📋 Widget config from parent:', initData.config);
            
            // Store parent origin if provided (for backend domain validation)
            // Note: Referer header will be widgets.staging.withinehr.com (iframe's URL),
            // not the parent page, so we need X-Parent-Origin from postMessage
            if (initData.config.parentOrigin && typeof initData.config.parentOrigin === 'string') {
              setParentOrigin(initData.config.parentOrigin);
              console.log('✅ Parent origin stored:', initData.config.parentOrigin);
            }
            
            // Store practice slug from postMessage (primary source)
            if (initData.config.practiceSlug && typeof initData.config.practiceSlug === 'string') {
              setPracticeSlug(initData.config.practiceSlug);
              console.log('✅ Practice slug stored from postMessage:', initData.config.practiceSlug);
            }
          }
          
          // Send ready message back to parent
          if (messaging) {
            messaging.send(MessageType.WIDGET_READY, {
              data: {
                version: '1.0.0',
                initialHeight: 600,
              },
            });
            console.log('✅ WIDGET_READY message sent to parent (in response to init)');
          }
        });

        // Send ready message to parent after a short delay to ensure iframe is fully loaded
        // This handles cases where init message arrives before handler is registered
        // Send multiple times to ensure it's received (with increasing delays)
        [100, 500, 1000].forEach((delay) => {
          setTimeout(() => {
            try {
              if (messaging) {
                messaging.send(MessageType.WIDGET_READY, {
                  data: {
                    version: '1.0.0',
                    initialHeight: 600,
                  },
                });
                console.log(`✅ WIDGET_READY message sent to parent (delay: ${delay}ms)`);
              }
            } catch (error) {
              console.error(`❌ Failed to send WIDGET_READY message (delay: ${delay}ms):`, error);
            }
          }, delay);
        });
      }

      console.log('✅ [App] Messaging service ready');
    } catch (error) {
      console.error('❌ [App] Failed to initialize messaging service:', error);
      // Still try to send a basic ready message
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'widget:ready',
            timestamp: new Date().toISOString(),
            messageId: `msg_${Date.now()}_fallback`,
            origin: window.location.origin,
            data: {
              version: '1.0.0',
              initialHeight: 600,
              source: 'fallback-after-error'
            }
          }, '*');
        }
      } catch (e) {
        console.error('❌ [App] Failed to send fallback ready message:', e);
      }
    }
    
    // Ensure messaging is available before using it
    if (!messaging) {
      console.error('❌ [App] Messaging service not available - cannot send messages');
      return;
    }

    // Initialize auto-resize service
    try {
      initResizeService({
        rootElement: document.getElementById('root') || undefined,
        debounceDelay: 100,
        minHeight: 400,
        maxHeight: 10000,
        debug: config.debug,
        onResize: (height) => {
          console.log('📏 Widget resized:', height);
        },
      });
      console.log('✅ Resize service initialized');
    } catch (error) {
      console.error('Failed to initialize resize service:', error);
    }

      setServicesInitialized(true);
    };

    initializeWidget().catch((error) => {
      console.error('❌ Failed to initialize widget:', error);
      setServicesInitialized(true); // Still set to true to show error UI
    });
  }, [initialize]);

  // Load widget settings data into store when fetched
  useEffect(() => {
    if (widgetSettingsData && !widgetLoading && !widgetError) {
      console.log('✅ Widget settings loaded:', widgetSettingsData);
      console.log('✅ Widget settings data structure:', {
        hasWidgetSettings: !!widgetSettingsData.widget_settings,
        hasPractice: !!widgetSettingsData.practice,
        servicesCount: widgetSettingsData.services?.length || 0,
        cliniciansCount: widgetSettingsData.clinicians?.length || 0,
        locationsCount: widgetSettingsData.locations?.length || 0,
        paymentMethodsCount: widgetSettingsData.payment_methods?.length || 0,
      });

      // Set widget data in store
      // Handle case where some data might be missing (simplified API response)
      setWidgetData({
        widgetSettings: widgetSettingsData.widget_settings,
        practice: widgetSettingsData.practice,
        services: widgetSettingsData.services || [],
        clinicians: widgetSettingsData.clinicians || [],
        locations: widgetSettingsData.locations || [],
        paymentMethods: widgetSettingsData.payment_methods || [],
      });
    }
  }, [widgetSettingsData, widgetLoading, widgetError, setWidgetData]);

  // Show loading while services initialize or widget data loads
  if (!servicesInitialized || widgetLoading) {
    return (
      <div className="ww-min-h-screen ww-bg-background ww-flex ww-items-center ww-justify-center">
        <LoadingSpinner
          text={widgetLoading ? 'Loading widget settings...' : 'Initializing widget...'}
        />
      </div>
    );
  }

  // Show error if widget settings failed to load
  if (widgetError) {
    return (
      <div className="ww-min-h-screen ww-bg-background ww-flex ww-items-center ww-justify-center">
        <ErrorDisplay
          error={`Failed to load widget settings: ${widgetError}`}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  // Handle booking confirmation
  const handleConfirm = (data: BookingData) => {
    setBookingData(data);
    setViewState('confirmation');
  };

  // Handle final confirmation
  const handleFinalConfirm = () => {
    setViewState('success');
  };

  // Handle close/reset
  const handleClose = () => {
    setViewState('booking');
    setBookingData(null);
  };

  // Get page title based on view state
  const getPageTitle = () => {
    switch (viewState) {
      case 'confirmation':
        return 'Confirm Your Appointment';
      case 'success':
        return 'Confirm Your Appointment';
      default:
        return 'Book Your Appointment';
    }
  };

  // Render content based on view state
  const renderContent = () => {
    // Handle loading state
    if (currentView === WidgetView.LOADING) {
      return (
        <div className="ww-flex ww-items-center ww-justify-center ww-min-h-[400px]">
          <LoadingSpinner />
        </div>
      );
    }

    // Handle error state
    if (currentView === WidgetView.ERROR) {
      return (
        <div className="ww-max-w-2xl ww-mx-auto">
          <ErrorDisplay
            error={error || 'An unexpected error occurred'}
            onRetry={() => {
              clearError();
              useWidgetStore.getState().setView(WidgetView.PROVIDER_SELECTION);
            }}
            onDismiss={clearError}
          />
        </div>
      );
    }

    // Render based on booking flow state
    switch (viewState) {
      case 'booking':
        return <BookingPage onConfirm={handleConfirm} />;

      case 'confirmation':
        if (!bookingData) return null;
        return (
          <div className="ww-grid ww-grid-cols-1 lg:ww-grid-cols-5 ww-gap-6">
            {/* Left Column - Same as booking but disabled */}
            <div className="lg:ww-col-span-3 ww-opacity-50 ww-pointer-events-none">
              <BookingPage onConfirm={handleConfirm} />
            </div>
            {/* Right Column - Confirmation Panel */}
            <div className="lg:ww-col-span-2">
              <ConfirmationPanel
                provider={bookingData.provider}
                slot={bookingData.slot}
                service={bookingData.service}
                location={bookingData.location}
                patientInfo={bookingData.patientInfo}
                paymentMethodId={bookingData.paymentMethodId}
                onConfirm={handleFinalConfirm}
                onBack={() => setViewState('booking')}
              />
            </div>
          </div>
        );

      case 'success':
        if (!bookingData) return null;
        return (
          <div className="ww-grid ww-grid-cols-1 lg:ww-grid-cols-5 ww-gap-6">
            {/* Left Column - Same as booking but disabled */}
            <div className="lg:ww-col-span-3 ww-opacity-50 ww-pointer-events-none">
              <BookingPage onConfirm={handleConfirm} />
            </div>
            {/* Right Column - Success Panel */}
            <div className="lg:ww-col-span-2">
              <SuccessPanel
                provider={bookingData.provider}
                slot={bookingData.slot}
                service={bookingData.service}
                onClose={handleClose}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Navbar */}
      <Navbar />

      <div className="ww-min-h-screen ww-bg-background ww-p-4 sm:ww-p-6 ww-pt-32 sm:ww-pt-36">
        <div className="ww-max-w-6xl ww-mx-auto">
          {/* Error: Missing Practice Slug */}
          {!resolvedPracticeSlug && (
            <div className="ww-mb-6 ww-p-4 ww-bg-red-50 ww-border ww-border-red-200 ww-rounded-lg">
              <h2 className="ww-text-lg ww-font-semibold ww-text-red-800 ww-mb-2">
                Practice Slug Required
              </h2>
              <p className="ww-text-red-700 ww-mb-2">
                The widget cannot load without a practice slug. Please provide it using one of these methods:
              </p>
              <ul className="ww-list-disc ww-list-inside ww-text-red-700 ww-space-y-1">
                <li>Via loader script: <code className="ww-bg-red-100 ww-px-1 ww-rounded">practiceSlug</code> parameter</li>
                <li>Via URL parameter: <code className="ww-bg-red-100 ww-px-1 ww-rounded">?slug=your-slug</code></li>
              </ul>
            </div>
          )}
          
          {/* Header */}
          <div className="ww-mb-6">
            <h1 className="ww-text-4xl sm:ww-text-4xl ww-font-medium ww-text-gray-900 ww-mb-1">
              {getPageTitle()}
            </h1>
            <p className="ww-text-gray-[#1B1B1B]">Schedule your visit with just simple steps</p>
          </div>

          {/* Main Content */}
          {renderContent()}
        </div>
      </div>
    </>
  );
}
